import { ReactNode, MouseEvent, forwardRef, useId } from 'react';

import classNames from 'classnames';
import MUIButton from '@mui/material/Button';

import { Box } from '../Box';
import { CircularProgress } from '../CircularProgress';

import useStyles from './Button.styles';

/**
 * Возможные варианты кнопки.
 */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiaryLight'
  | 'tertiaryDark'
  | 'tertiaryBlue'
  | 'quaternary'
  | 'quaternaryAccent'
  | 'quaternaryDark'
  | 'warning';

/**
 * Возможные размеры кнопки.
 */
export type ButtonSize = 'large' | 'medium' | 'small' | 'extraSmall';

/**
 * Свойства компонента {@link Button}
 */
export interface ButtonProps {
  /**
   * Класс для кнопки.
   */
  className?: string;

  /**
   * ID для кнопки.
   */
  id?: string;

  /**
   * Содержимое, отображаемое внутри кнопки.
   */
  children?: ReactNode | ReactNode[];

  /**
   * Тип кнопки.
   *
   * @defaultValue `'button'`
   */
  type?: 'button' | 'submit';

  /**
   * Определяет, должна ли кнопка быть отключена.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;

  /**
   * Определяет, должна ли быть отключена волна (ripple) эффект.
   *
   * @defaultValue `false`
   */
  disableRipple?: boolean;

  /**
   * Иконка, отображаемая в конце кнопки.
   */
  icon?: ReactNode;

  /**
   * Иконка, отображаемая в начале кнопки.
   */
  startIcon?: ReactNode;

  /**
   * Определяет, должна ли кнопка занимать всю ширину своего контейнера.
   *
   * @defaultValue `false`
   */
  fullWidth?: boolean;

  /**
   * Показывает индикатор загрузки и отключает взаимодействие с кнопкой.
   *
   * @defaultValue `false`
   */
  loading?: boolean;

  /**
   * Размер кнопки.
   *
   * @defaultValue `'medium'`
   */
  size?: ButtonSize;

  /**
   * Текст, отображаемый внутри кнопки.
   */
  text?: string;

  /**
   * Вариант кнопки.
   *
   * @defaultValue `'primary'`
   */
  variant?: ButtonVariant;

  /**
   * Обработчик события клика.
   */
  onClick?: (e: MouseEvent<HTMLElement>) => void;

  /**
   * Тестовый ID для кнопки.
   */
  testId?: string;
}

const muiVariants: Record<ButtonVariant, 'text' | 'outlined' | 'contained'> = {
  primary: 'contained',
  quaternary: 'contained',
  quaternaryAccent: 'contained',
  quaternaryDark: 'contained',
  warning: 'contained',
  secondary: 'outlined',
  tertiaryLight: 'text',
  tertiaryDark: 'text',
  tertiaryBlue: 'text',
};

const aqaClasses: Record<ButtonVariant, string> = {
  primary: 'aqa_button_primary',
  secondary: 'aqa_button_secondary',
  quaternary: 'aqa_button_quaternary',
  quaternaryAccent: 'aqa_button_quaternary',
  warning: 'aqa_button_warning',
  tertiaryDark: 'aqa_button_tertiaryDark',
  tertiaryBlue: 'aqa_button_tertiaryBlue',
  tertiaryLight: '',
  quaternaryDark: '',
};

/**
 * Настраиваемая кнопка, построенная с использованием Material-UI.
 *
 * @public
 * @param className - Класс для кнопки.
 * @param id - ID для кнопки.
 * @param children - Содержимое, отображаемое внутри кнопки.
 * @param type - Тип кнопки.
 * @param disabled - Определяет, должна ли кнопка быть отключена.
 * @param disableRipple - Определяет, должна ли быть отключена волна (ripple) эффект.
 * @param icon - Иконка, отображаемая в конце кнопки.
 * @param startIcon - Иконка, отображаемая в начале кнопки.
 * @param fullWidth - Определяет, должна ли кнопка занимать всю ширину своего контейнера.
 * @param loading - Показывает индикатор загрузки и отключает взаимодействие с кнопкой.
 * @param size - Размер кнопки.
 * @param text - Текст, отображаемый внутри кнопки.
 * @param variant - Вариант кнопки.
 * @param onClick - Обработчик события клика.
 * @param testId - Тестовый ID для кнопки.
 * @returns Компонент Button.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link ButtonProps}
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      id,
      children,
      type = 'button',
      disabled = false,
      disableRipple = false,
      icon,
      startIcon,
      fullWidth = false,
      loading = false,
      size = 'medium',
      variant = 'primary',
      onClick,
      testId,
      ...rest // we need this for tooltips
    },
    ref,
  ) => {
    const { classes } = useStyles({ disabled });
    const generatedId = useId();
    const buttonId = id ?? generatedId;

    const buttonStyles = classNames(
      classes.button,
      classes[variant],
      !!icon &&
        !children &&
        `${classes.buttonIconOnly}${
          variant === 'primary' ? ` ${classes.buttonPrimaryIconOnly}` : ''
        }`,
      className,
      'aqa_button',
      aqaClasses[variant],
      size === 'extraSmall' && classes.extraSmall,
    );

    const muiVariant: 'text' | 'outlined' | 'contained' =
      muiVariants[variant] || variant;

    return (
      <MUIButton
        id={buttonId}
        data-testid={testId}
        color="primary"
        variant={muiVariant}
        className={buttonStyles}
        disabled={disabled}
        loading={loading}
        loadingIndicator={
          <CircularProgress
            aria-labelledby={buttonId}
            size={size === 'extraSmall' ? 12.6 : 18.8}
            color="inherit"
          />
        }
        disableRipple={disableRipple}
        fullWidth={fullWidth}
        startIcon={startIcon}
        endIcon={icon}
        size={size === 'extraSmall' ? 'small' : size}
        type={type}
        onClick={onClick}
        ref={ref}
        {...rest}
      >
        <Box className={classes.content}>{children}</Box>
      </MUIButton>
    );
  },
);
